<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Input, Select, Textarea } from '@drama/ui'
import NovelImageField from '../NovelImageField.vue'
import FormField from '../NovelFormField.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import { assetTag, newLocation } from '@drama/novel-authoring'
import { useSectionModel } from './section-model.js'
import { useEntitySelection } from './entity-selection.js'
import './novel-sections.css'

const props = defineProps({
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  upload: { type: Function, required: true },
  guided: { type: Boolean, default: false },
  focus_target: { type: Object, default: null },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'blocked'])
const { t } = useI18n()
const { bind, invalid, notes, removeEntity, write, error } = useSectionModel(props, emit)
const places = computed(() => props.modelValue.locations ?? [])
const { selected_id } = useEntitySelection(props, 'locations')
const location_options = computed(() => places.value.map((place, index) => ({
  value: place.id, label: place.name || `${t('novelStudio.fields.newLocation')} ${index + 1}`,
})))

function addLocation() {
  const place = newLocation(props.modelValue, t('novelStudio.fields.newLocation'))
  write(['locations'], [...places.value, place])
  selected_id.value = place.id
}

/** 地点图是可选键：清空即删键。 */
function setImage(index, url) {
  const place = { ...places.value[index] }
  if (url) place.image = url
  else delete place.image
  write(['locations', index], place)
}
</script>

<template>
  <section
    class="novel-entities"
    :class="{ 'novel-entity-picker': guided }"
    data-testid="novel-locations-section"
  >
    <header class="novel-entities__header">
      <div class="novel-entities__heading">
        <h3 v-if="guided">{{ t('novelStudio.sections.locations') }}</h3>
        <p>{{ t('novelStudio.fields.count', { count: places.length }) }}</p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        data-testid="novel-add-location"
        @click="addLocation"
      >
        {{ t('novelStudio.fields.addLocation') }}
      </Button>
    </header>
    <Select
      v-if="guided && places.length"
      v-model="selected_id"
      :options="location_options"
      :aria-label="t('novelAuthoring.selectLocation')"
      :disabled="disabled"
      block
      data-testid="novel-location-select"
    />
    <p v-if="!guided" class="novel-entities__hint">
      {{ t('novelStudio.fields.referenceHelp') }}
    </p>
    <p
      v-if="!places.length"
      class="novel-entities__empty"
    >
      {{ t('novelStudio.empty.locations') }}
    </p>
    <article
      v-for="(place, index) in places"
      v-show="!guided || place.id === selected_id"
      :key="`${place.id}-${index}`"
      class="novel-entity"
      :class="{ 'has-error': invalid(['locations', index]) }"
      :data-path="`locations.${index}`"
      tabindex="-1"
      :data-testid="`novel-location-${index}`"
    >
      <header class="novel-entity__header">
        <div class="novel-entity__title">
          <h3>{{ place.name || place.id }}</h3>
          <code v-if="!guided">{{ place.id }}</code>
        </div>
        <Button
          variant="secondary"
          size="sm"
          :aria-label="t('novelStudio.fields.removeNamed', { name: place.name || place.id })"
          :data-testid="`novel-location-${index}-remove`"
          @click="removeEntity('locations', index, place.name || place.id)"
        >
          {{ t('novelStudio.fields.remove') }}
        </Button>
      </header>
      <NovelIssueNote :notes="notes(['locations', index])" />
      <NovelImageField
        :model-value="place.image || ''"
        :label="t('novelStudio.fields.image')"
        :tag="assetTag('location', place.id)"
        :upload="upload"
        :alt="place.name || place.id"
        :invalid="invalid(['locations', index, 'image'])"
        :data-path="`locations.${index}.image`"
        @update:model-value="setImage(index, $event)"
      />
      <FormField
        :error="error(['locations', index, 'name'])"
        required
        :label="t('novelStudio.fields.name')"
      >
        <Input
          v-bind="bind(['locations', index, 'name'])"
          :data-testid="`novel-location-${index}-name`"
        />
      </FormField>
      <FormField
        :error="error(['locations', index, 'description'])"
        required
        :label="t('novelStudio.fields.description')"
      >
        <Textarea
          v-bind="bind(['locations', index, 'description'])"
          :rows="3"
        />
      </FormField>
    </article>
  </section>
</template>
